import { clearDeliverySessionStorage } from '../services/activeDeliverySession';

declare const __APP_VERSION__: string;
declare const __APP_BUILD_ID__: string;

const BUILD_ID_KEY = 'go7-app-build-id';
const RELOAD_GUARD_KEY = 'go7-cache-reload-guard';

export const APP_VERSION = __APP_VERSION__;
export const APP_BUILD_ID = __APP_BUILD_ID__;

export interface RemoteVersionInfo {
  version: string;
  buildId: string;
  builtAt: string;
}

/** Run once before React mounts — clears stale session when a new build loads */
export function ensureFreshAppOnBoot(): void {
  const storedBuildId = localStorage.getItem(BUILD_ID_KEY);

  if (!storedBuildId) {
    localStorage.setItem(BUILD_ID_KEY, APP_BUILD_ID);
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
    return;
  }

  if (storedBuildId !== APP_BUILD_ID) {
    clearDeliverySessionStorage();
    localStorage.setItem(BUILD_ID_KEY, APP_BUILD_ID);
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  }
}

export async function fetchRemoteVersion(): Promise<RemoteVersionInfo | null> {
  try {
    const base = import.meta.env.BASE_URL || '/';
    const url = `${base}version.json?ts=${Date.now()}`;
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return null;
    return (await response.json()) as RemoteVersionInfo;
  } catch {
    return null;
  }
}

export function isRemoteVersionNewer(remote: RemoteVersionInfo | null): boolean {
  return Boolean(remote?.buildId && remote.buildId !== APP_BUILD_ID);
}

/** Reload to pick up a newly deployed bundle (guarded against infinite loops) */
export function reloadForAppUpdate(): void {
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return;

  sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  clearDeliverySessionStorage();

  const url = new URL(window.location.href);
  url.searchParams.set('v', String(Date.now()));
  window.location.replace(url.toString());
}
